import { FormEvent, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import {
    deleteSshProfile,
    getSshProfiles,
    saveSshProfile,
    SshAuthType,
    SshProfile,
} from "../../api/sshProfiles";
import SshConnectionTest from "./SshConnectionTest";

type ProfileDraft = {
    name: string;
    sshHost: string;
    sshPort: string;
    sshUsername: string;
    sshAuthType: SshAuthType;
    sshPrivateKeyPath: string;
    strictHostKeyChecking: boolean;
};

const emptyDraft = (): ProfileDraft => ({
    name: "",
    sshHost: "",
    sshPort: "22",
    sshUsername: "",
    sshAuthType: "PASSWORD",
    sshPrivateKeyPath: "",
    strictHostKeyChecking: true,
});

const toDraft = (profile: SshProfile): ProfileDraft => ({
    name: profile.name,
    sshHost: profile.sshHost,
    sshPort: String(profile.sshPort),
    sshUsername: profile.sshUsername,
    sshAuthType: profile.sshAuthType,
    sshPrivateKeyPath: profile.sshPrivateKeyPath ?? "",
    strictHostKeyChecking: profile.strictHostKeyChecking,
});

const errorMessage = (error: unknown) => {
    const data = (error as { response?: { data?: { detail?: string; message?: string } } }).response?.data;
    return data?.detail || data?.message || "SSH 프로필을 저장하지 못했습니다.";
};

export default function SshProfileManagerModal({
    show,
    initialProfileId,
    onClose,
}: {
    show: boolean;
    initialProfileId?: number | null;
    onClose: () => void;
    focusSiteId?: number | null;
}) {
    const [profiles, setProfiles] = useState<SshProfile[]>([]);
    const [selectedId, setSelectedId] = useState<number | "new" | null>(null);
    const [draft, setDraft] = useState<ProfileDraft | null>(null);
    const [searchText, setSearchText] = useState("");
    const [credential, setCredential] = useState("");
    const [savePassword, setSavePassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const loadProfiles = async (preferredId?: number | null) => {
        setIsLoading(true);
        try {
            const loadedProfiles = await getSshProfiles();
            const selected = loadedProfiles.find((profile) => profile.id === preferredId) ?? loadedProfiles[0] ?? null;
            setProfiles(loadedProfiles);
            setSelectedId(selected?.id ?? "new");
            setDraft(selected ? toDraft(selected) : emptyDraft());
            setSavePassword(selected?.hasStoredSshPassword === true);
            setCredential("");
        } catch (error) {
            console.error("SSH profile load failed", error);
            await Swal.fire({ icon: "error", title: "SSH 프로필 조회 실패", text: errorMessage(error) });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        setSearchText("");
        void loadProfiles(initialProfileId);
        // loadProfiles is intentionally scoped to each modal open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialProfileId, show]);

    const selectedProfile = useMemo(
        () => typeof selectedId === "number" ? profiles.find((profile) => profile.id === selectedId) ?? null : null,
        [profiles, selectedId]
    );
    const filteredProfiles = useMemo(() => {
        const query = searchText.trim().toLocaleLowerCase();
        if (!query) return profiles;
        return profiles.filter((profile) =>
            [profile.name, profile.sshHost, profile.sshUsername]
                .some((value) => value.toLocaleLowerCase().includes(query))
        );
    }, [profiles, searchText]);
    const credentialDirty = Boolean(
        draft?.sshAuthType === "PASSWORD" &&
        (savePassword !== (selectedProfile?.hasStoredSshPassword === true) || (savePassword && credential))
    );
    const isDirty = Boolean(
        draft && (
            selectedId === "new" ||
            (selectedProfile && JSON.stringify(toDraft(selectedProfile)) !== JSON.stringify(draft)) ||
            credentialDirty
        )
    );

    if (!show) return null;

    const updateDraft = <K extends keyof ProfileDraft>(field: K, value: ProfileDraft[K]) => {
        setDraft((current) => current ? { ...current, [field]: value } : current);
        if (field === "sshAuthType") {
            setCredential("");
            setSavePassword(value === "PASSWORD" && selectedProfile?.hasStoredSshPassword === true);
        }
    };

    const confirmDiscard = async () => {
        if (!isDirty) return true;
        const result = await Swal.fire({
            icon: "warning",
            title: "저장하지 않은 변경 사항",
            text: "변경 내용을 저장하지 않고 이동할까요?",
            showCancelButton: true,
            confirmButtonText: "이동",
            cancelButtonText: "취소",
            reverseButtons: true,
        });
        return result.isConfirmed;
    };

    const selectProfile = async (profile: SshProfile) => {
        if (profile.id === selectedId || !(await confirmDiscard())) return;
        setSelectedId(profile.id);
        setDraft(toDraft(profile));
        setCredential("");
        setSavePassword(profile.hasStoredSshPassword === true);
    };

    const createProfile = async () => {
        if (selectedId === "new" || !(await confirmDiscard())) return;
        setSelectedId("new");
        setDraft(emptyDraft());
        setCredential("");
        setSavePassword(false);
    };

    const requestClose = async () => {
        if (await confirmDiscard()) onClose();
    };

    const save = async (event: FormEvent) => {
        event.preventDefault();
        if (!draft || isSaving) return;
        const port = Number(draft.sshPort);
        if (!draft.name.trim() || !draft.sshHost.trim() || !draft.sshUsername.trim()) {
            await Swal.fire({ icon: "warning", title: "입력 확인", text: "프로필 이름, SSH 호스트, SSH 계정을 입력해 주세요." });
            return;
        }
        if (!Number.isInteger(port) || port < 1 || port > 65535) {
            await Swal.fire({ icon: "warning", title: "입력 확인", text: "SSH 포트는 1~65535 범위로 입력해 주세요." });
            return;
        }
        if (draft.sshAuthType === "PRIVATE_KEY" && !draft.sshPrivateKeyPath.trim()) {
            await Swal.fire({ icon: "warning", title: "입력 확인", text: "SSH 개인키 경로를 입력해 주세요." });
            return;
        }
        if (draft.sshAuthType === "PASSWORD" && savePassword && !credential && !selectedProfile?.hasStoredSshPassword) {
            await Swal.fire({ icon: "warning", title: "입력 확인", text: "저장할 SSH 비밀번호를 입력해 주세요." });
            return;
        }

        setIsSaving(true);
        try {
            const saved = await saveSshProfile({
                ...(typeof selectedId === "number" ? { id: selectedId } : {}),
                name: draft.name.trim(),
                sshHost: draft.sshHost.trim(),
                sshPort: port,
                sshUsername: draft.sshUsername.trim(),
                sshAuthType: draft.sshAuthType,
                sshPrivateKeyPath: draft.sshAuthType === "PRIVATE_KEY" ? draft.sshPrivateKeyPath.trim() : undefined,
                strictHostKeyChecking: draft.strictHostKeyChecking,
                saveSshPassword: draft.sshAuthType === "PASSWORD" ? savePassword : false,
                sshPassword: draft.sshAuthType === "PASSWORD" && credential ? credential : undefined,
            });
            await loadProfiles(saved.id);
            await Swal.fire({ icon: "success", title: "SSH 프로필을 저장했습니다.", timer: 1000, showConfirmButton: false });
        } catch (error) {
            console.error("SSH profile save failed", error);
            await Swal.fire({
                icon: "error",
                title: "SSH 프로필 저장 실패",
                text: errorMessage(error),
            });
        } finally {
            setIsSaving(false);
        }
    };

    const remove = async () => {
        if (!selectedProfile || isSaving) return;
        if ((selectedProfile.linkedPathCount ?? 0) > 0) {
            await Swal.fire({
                icon: "warning",
                title: "사용 중인 SSH 프로필",
                text: `이 프로필을 사용하는 배포 경로가 ${selectedProfile.linkedPathCount}개 있습니다. 경로 연결을 먼저 해제해 주세요.`,
            });
            return;
        }
        const result = await Swal.fire({
            icon: "warning",
            title: "SSH 프로필 삭제",
            text: `${selectedProfile.name} 프로필을 삭제할까요?`,
            showCancelButton: true,
            confirmButtonText: "삭제",
            cancelButtonText: "취소",
            reverseButtons: true,
        });
        if (!result.isConfirmed) return;
        try {
            await deleteSshProfile(selectedProfile.id);
            await loadProfiles();
        } catch (error) {
            await Swal.fire({ icon: "error", title: "SSH 프로필 삭제 실패", text: errorMessage(error) });
        }
    };

    return (
        <div className="modal dp-modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered dp-modal-dialog dp-path-manager-dialog">
                <div className="modal-content dp-modal-content dp-path-manager dp-ssh-profile-manager">
                    <div className="modal-header dp-modal-header dp-path-manager-header">
                        <div><h5 className="modal-title">SSH 관리</h5><span>{profiles.length}개 프로필</span></div>
                        <button type="button" className="btn-close" aria-label="닫기" onClick={() => void requestClose()} />
                    </div>
                    <div className="dp-path-manager-body">
                        <aside className="dp-path-manager-sidebar">
                            <div className="dp-path-manager-sidebar-title"><strong>SSH 프로필</strong><button type="button" className="dp-profile-add" onClick={() => void createProfile()}>+ 새 프로필</button></div>
                            <input type="search" className="dp-path-manager-search" aria-label="SSH 프로필 검색" placeholder="호스트 또는 계정 검색" value={searchText} onChange={(event) => setSearchText(event.currentTarget.value)} />
                            <div className="dp-path-manager-list" role="listbox">
                                {filteredProfiles.map((profile) => (
                                    <button key={profile.id} type="button" role="option" aria-selected={profile.id === selectedId} className={`dp-path-manager-item${profile.id === selectedId ? " is-active" : ""}`} onClick={() => void selectProfile(profile)}>
                                        <span className="dp-path-manager-status is-connected" />
                                        <span className="dp-path-manager-item-copy"><strong>{profile.name}</strong><small>{profile.sshUsername}@{profile.sshHost}:{profile.sshPort}</small></span>
                                        {(profile.linkedPathCount ?? 0) > 0 && <em className="dp-profile-link-count">{profile.linkedPathCount}</em>}
                                    </button>
                                ))}
                                {!isLoading && profiles.length === 0 && <div className="dp-path-manager-empty">등록된 SSH 프로필이 없습니다.</div>}
                            </div>
                        </aside>
                        <section className="dp-path-manager-detail">
                            {draft ? (
                                <form className="dp-path-manager-form" onSubmit={save}>
                                    <div className="dp-path-manager-detail-header">
                                        <div><span>{selectedId === "new" ? "새 프로필" : "선택한 프로필"}</span><h6>{draft.name || "SSH 연결 정보 입력"}</h6></div>
                                        {selectedProfile && <div className="dp-profile-usage"><strong>{selectedProfile.linkedPathCount ?? 0}</strong><span>연결된 배포 경로</span></div>}
                                    </div>
                                    <div className="dp-path-manager-ssh-fields dp-ssh-profile-fields">
                                        <label className="dp-ssh-field dp-ssh-profile-name"><span>프로필 이름</span><input className="form-control" value={draft.name} placeholder="예: 운영 서버 SSH" onChange={(event) => updateDraft("name", event.currentTarget.value)} /></label>
                                        <div className="dp-ssh-basic-grid">
                                            <label className="dp-ssh-field dp-ssh-host-field"><span>SSH 호스트</span><input className="form-control" value={draft.sshHost} placeholder="192.168.0.10 또는 host.example.com" onChange={(event) => updateDraft("sshHost", event.currentTarget.value)} /></label>
                                            <label className="dp-ssh-field dp-ssh-port-field"><span>포트</span><input className="form-control" type="number" min={1} max={65535} value={draft.sshPort} onChange={(event) => updateDraft("sshPort", event.currentTarget.value)} /></label>
                                            <label className="dp-ssh-field dp-ssh-user-field"><span>SSH 계정</span><input className="form-control" value={draft.sshUsername} placeholder="deployer" onChange={(event) => updateDraft("sshUsername", event.currentTarget.value)} /></label>
                                            <label className="dp-ssh-field"><span>인증 방식</span><select className="form-select" value={draft.sshAuthType} onChange={(event) => updateDraft("sshAuthType", event.currentTarget.value as SshAuthType)}><option value="PASSWORD">비밀번호</option><option value="PRIVATE_KEY">개인키</option></select></label>
                                        </div>
                                        {draft.sshAuthType === "PRIVATE_KEY" && <label className="dp-ssh-field"><span>개인키 경로</span><input className="form-control" value={draft.sshPrivateKeyPath} placeholder="C:\\Users\\user\\.ssh\\id_ed25519" onChange={(event) => updateDraft("sshPrivateKeyPath", event.currentTarget.value)} /></label>}
                                        <label className="dp-path-toggle dp-ssh-host-key-toggle"><input type="checkbox" checked={draft.strictHostKeyChecking} onChange={(event) => updateDraft("strictHostKeyChecking", event.currentTarget.checked)} /><span>known_hosts 호스트 키 검증</span></label>
                                        <SshConnectionTest
                                            settings={draft}
                                            profileId={selectedProfile?.id}
                                            credentialValue={credential}
                                            onCredentialValueChange={setCredential}
                                            savePassword={savePassword}
                                            onSavePasswordChange={setSavePassword}
                                            hasStoredPassword={selectedProfile?.hasStoredSshPassword === true && savePassword}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className="dp-path-manager-actions">
                                        {selectedProfile ? <button type="button" className="dp-path-manager-delete" onClick={() => void remove()} disabled={isSaving}>삭제</button> : <span />}
                                        <div className="dp-path-manager-action-group">
                                            <button type="button" className="dp-path-manager-close" onClick={() => void requestClose()}>닫기</button>
                                            <button type="submit" className="dp-path-manager-save" disabled={!isDirty || isSaving}>{isSaving ? "저장 중" : selectedId === "new" ? "SSH 프로필 등록" : "SSH 설정 저장"}</button>
                                        </div>
                                    </div>
                                </form>
                            ) : <div className="dp-path-manager-detail-empty"><strong>SSH 프로필을 선택해 주세요.</strong></div>}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}
